import { ColumnsType } from "antd/es/table";
import { Dispatch, SetStateAction, useContext } from "react";
import { useTranslation } from "react-i18next";
import SText from "@/styles/SText";
import { Shipment } from "@/types/shipment";
import { dateFormat } from "@/utils/format";
import dayjs from "dayjs";
import { DictContext } from "@/layout/context/DictContext";
import { DictEnum } from "@/types/dict";
import { CountrySelect } from "@/components/Selector";
import { Typography } from "antd";

interface ShipColProps {
  openDetail: Dispatch<SetStateAction<Shipment | null>>
}
const useShipmentColumns = (props: ShipColProps): ColumnsType<Shipment> => {
  const { openDetail } = props;
  const { t } = useTranslation();
  const { shipStatus } = useContext(DictContext);

  const columns: ColumnsType<Shipment> = [
    {
      title: t("shipment.orderNumber"),
      key: "number",
      dataIndex: "number",
      width: 180,
      fixed: "left",
      render: (_, record) => <Typography.Link onClick={() => openDetail(record)} >{record.number}</Typography.Link>,
    },
    {
      title: t("shipment.customerName"),
      key: "customerName",
      dataIndex: "customerName",
      width: 150,
      render: (_, record) => <SText text={record.customerName ?? ""} />,
    },
    {
      title: t("shipment.status"),
      key: "status",
      dataIndex: "status",
      width: 100,
      render: (status) => {
        const statusItem = shipStatus.find(s => s.key === status);
        return <SText text={statusItem?.desc ?? String(status ?? "")} />;
      },
    },
    {
      title: t("shipment.consignee"),
      key: "consignee",
      dataIndex: "consignee",
      width: 120,
      render: (_, record) => <SText text={record.consignee ?? ""} />,
    },
    {
      title: t("shipment.consigneePhone"),
      key: "consigneePhone",
      dataIndex: "consigneePhone",
      width: 130,
      render: (_, record) => <SText text={record.consigneePhone ?? ""} />,
    },
    {
      title: t("shipment.destinationCountry"),
      key: "destinationCountry",
      dataIndex: "destinationCountry",
      width: 120,
      render: (_, record) => <CountrySelect readOnly value={record.destinationCountry} />,
    },
    {
      title: t("shipment.planShipDate"),
      key: "planShipDate",
      dataIndex: "planShipDate",
      width: 130,
      render: (_, record) => (
        <SText text={record.planShipDate ? dateFormat(dayjs(record.planShipDate)) : ""} />
      ),
    },
    {
      title: t("shipment.actualShipDate"),
      key: "actualShipDate",
      dataIndex: "actualShipDate",
      width: 130,
      render: (_, record) => (
        <SText text={record.actualShipDate ? dateFormat(dayjs(record.actualShipDate)) : ""} />
      ),
    },
    {
      title: t("shipment.deliveredAt"),
      key: "deliveredAt",
      dataIndex: "deliveredAt",
      width: 130,
      render: (_, record) => (
        <SText text={record.deliveredAt ? dateFormat(dayjs(record.deliveredAt)) : ""} />
      ),
    },
    {
      title: t("shipment.paymentTerms"),
      key: "paymentTerms",
      dataIndex: "paymentTerms",
      width: 120,
      render: (paymentTerms) => <SText text={paymentTerms ?? ""} />,
    },
    {
      title: t("shipment.tradeTerms"),
      key: "tradeTerms",
      dataIndex: "tradeTerms",
      width: 100,
      render: (_, record) => <SText text={record.tradeTerms ?? ""} />,
    },
    {
      title: t("shipment.remark"),
      key: "remark",
      dataIndex: "remark",
      width: 150,
      render: (_, record) => <SText text={record.remark ?? ""} />,
    },
  ];

  return columns;
};

export default useShipmentColumns;
